import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import { looseEqual } from '../../lib';
import ClIcon from '../ClIcon';
import ClIconButton from '../ClIconButton';
import ClRadioGroup from '../ClRadioGroup';

@Component({
  components: { ClIcon, ClIconButton }
})
export default class ClRadio extends Vue {
  /**
   * If true, the input will be focused during the first mount.
   */
  @Prop({
    type: Boolean
  })
  public autoFocus!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  public checked!: boolean;

  public checkedState: string | object | number | boolean = this.checked;

  /**
   * Props for cl-icon component
   */
  @Prop(Object)
  public clIconProps?: object;

  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   */
  @Prop({
    type: String,
    validator: x => ['primary', 'secondary', 'default'].includes(x),
    default: 'primary'
  })
  public color!: 'primary' | 'secondary' | 'default';

  @Prop({
    type: Boolean,
    default: false
  })
  public disabled!: boolean;

  @Prop(String)
  public id!: string;

  /**
   * Props for the input element
   */
  @Prop(Object)
  public inputProps!: object;

  @Prop(String)
  public label!: string;

  @Prop(String)
  public name!: string;

  @Prop(Boolean)
  public required!: boolean;

  /**
   * The type attribute of the input element.
   */
  @Prop({
    type: String,
    default: 'radio'
  })
  public type!: string;

  @Prop({
    type: String,
    default: ''
  })
  public value!: string;

  get isChecked(): boolean {
    if (!this.value) {
      return Boolean(this.computedCheckedState);
    }
    return looseEqual(this.computedCheckedState, this.value);
  }

  getGroupComponent(comp: Vue | null): Vue | null {
    if (!comp) {
      return null;
    }
    if ((comp as any).isGroup) {
      return comp;
    }
    return this.getGroupComponent(comp.$parent);
  }

  get group(): Vue | null {
    let gc: Vue | null = this.$parent
      ? (this.$parent.$parent as Vue | null)
      : null;
    return this.getGroupComponent(gc) as Vue | null;
  }

  get computedName() {
    const group = this.group as any;
    return (group ? group.name || group.id : this.name) || null;
  }

  get computedCheckedState() {
    const group = this.group as any;
    if (group) {
      return group.checkedState;
    } else {
      return this.checkedState;
    }
  }

  set computedCheckedState(val) {
    const group = this.group as any;
    if (group) {
      group.checkedState = val;
    } else {
      this.checkedState = val;
    }
  }

  get isInGroup(): boolean {
    const group = this.group as any;
    return Boolean(group && group.isGroup);
  }

  get isDisabled(): boolean {
    const group = this.group as any;
    // Child can be disabled while parent isn't
    return Boolean(group ? group.disabled || this.disabled : this.disabled);
  }

  get isRequired() {
    const group = this.group as any;
    return Boolean(group ? (group as any).required : this.required);
  }

  @Watch('checked')
  watchChecked(checked: boolean) {
    this.computedCheckedState = checked;
  }

  @Watch('computedCheckedState')
  watchComputedCheckedState() {
    this.$emit('input', this.computedCheckedState);
  }

  emitChange(event: Event) {
    const { checked } = event.target as HTMLInputElement;
    // Change is only emitted on user interaction
    this.$emit('change', checked ? this.value : null);
    // If this is a child of form-radio-group, we emit a change event on it as well
    if (this.group) {
      this.group.$emit('change', this.computedCheckedState);
    }
  }
}
